import React, { useEffect, useState } from 'react';
import { BellRing } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ColumnHeader } from './columnChrome';
import { ReminderRow } from '../RemindersDropdown';
import DocumentPreviewModal from '../DocumentPreviewModal';
import { useLanguage } from '../../i18n';

// ─────────────────────────────────────────────────────────────────────────
// NotificationsColumn — the right-hand column of the "Reminders &
// Notifications" panel (#437).
//
// The Bell used to be a small tray hung off the Main Chat header. The card moved
// it here and asked for uniformity: every notification is now a box the SAME
// size and style as a Reminders card, with a clear X that removes it at once.
// Only the container and the location changed — the feed (what appears, when,
// the 2-minute poll, the live refetch, dismissal semantics, Confirm/Decline on
// invitations and contact requests) is the Bell's own, via useNotificationFeed
// and the very same ReminderRow in its 'box' variant.
//
// The feed's DATA lives in WorkspaceShell (passed in as `feed`) so the sidebar's
// red dot and this column always read the same list.
//
// ⚠️ The list keeps the Bell's `bell-dropdown` testid (and every row testid —
// `bell-item`, `bell-dismiss`, `bell-system-notification`, …): it is the same
// list of the same rows, and keeping the handles is what let the existing
// notification specs follow it here with a one-line change to how they open it.
// ─────────────────────────────────────────────────────────────────────────

const NotificationsColumn = ({ feed }) => {
    const { t } = useLanguage();
    const navigate = useNavigate();
    const [preview, setPreview] = useState(null);
    const { items = [], loading, error, refresh, clear } = feed || {};

    // Opening the feature refetches at once — the old tray did the same on open.
    useEffect(() => { refresh?.(); }, [refresh]);

    // Escape closes the PREVIEW, never the panel behind it (the #272 lesson).
    useEffect(() => {
        if (!preview) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') { e.stopPropagation(); setPreview(null); }
        };
        document.addEventListener('keydown', onKey, true);
        return () => document.removeEventListener('keydown', onKey, true);
    }, [preview]);

    const onBodyClick = (item) => {
        if (item.link) navigate(item.link);
    };

    return (
        <div data-testid="notifications-column" className="flex-1 min-w-0 min-h-0 flex flex-col pai-card overflow-hidden">
            <ColumnHeader
                icon={<BellRing size={18} />}
                accent="blue"
                title={t('notificationsColumnTitle', 'Notifications')}
                count={items.length}
                t={t}
            />
            <div data-testid="bell-dropdown" className="flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-2.5">
                {error && (
                    <div className="px-1 text-[12px] text-pai-red">{error}</div>
                )}
                {loading && items.length === 0 && !error && (
                    <div className="text-center text-[13px] text-white/40 py-8">
                        {t('navLoadingEllipsis', 'Loading…')}
                    </div>
                )}
                {!loading && items.length === 0 && !error && (
                    <div data-testid="notifications-empty" className="text-center text-[13px] text-white/40 py-8">
                        {t('notificationsEmpty', 'No notifications.')}
                    </div>
                )}
                {items.map((item) => (
                    <ReminderRow
                        key={`${item.type}-${item.id}`}
                        variant="box"
                        item={item}
                        onBodyClick={onBodyClick}
                        onClear={(it, e) => { e.stopPropagation(); clear?.(it); }}
                        onOpenAttachment={setPreview}
                        onAfterRespond={() => {
                            refresh?.();
                            // Responding flips the event and (on accept) dual-writes it
                            // onto this user's calendar — refresh any open calendar.
                            window.dispatchEvent(new CustomEvent('omnis:calendar-event-updated'));
                        }}
                    />
                ))}
            </div>
            {preview && (
                <DocumentPreviewModal
                    documentId={preview.document_id}
                    documentName={preview.filename}
                    onClose={() => setPreview(null)}
                />
            )}
        </div>
    );
};

export default NotificationsColumn;
